/**
 * PHI redaction for structured logs.
 *
 * HealthOS handles Protected Health Information (vitals, glucose, blood
 * pressure, weight, medication and injection logs, mental-health scores,
 * lab values). None of it may reach stdout, and therefore Datadog. This
 * scrubber masks PHI at the single logger choke point so existing call
 * sites need no changes.
 *
 * Deny-list, two tiers (see ADR-0021):
 *  - CONTAINER_KEYS: keys whose value is a PHI-bearing object or array.
 *    The whole value is masked without descending into it.
 *  - LEAF_KEYS: health and identifier keys masked anywhere in the tree.
 * Keys on neither list pass through and are recursed into, so safe
 * operational fields (userId, resource ids, status, method, path,
 * component, ...) survive simply by not being listed.
 *
 * Deny-list is a deliberate choice: lower developer friction, with the
 * container tier and a CI lint guard compensating for the open-by-default
 * risk an allow-list would avoid.
 */
export const REDACTED = "[REDACTED]";
const TRUNCATED = "[TRUNCATED]";
// Guards against pathological objects (cycles via depth, huge fan-out via
// entries). Logging must never stall on a giant context object.
const MAX_DEPTH = 8;
const MAX_ENTRIES = 200;
// Tier 1: whole subtree masked. These keys carry PHI-bearing objects or
// arrays; we never descend into them.
const CONTAINER_KEYS = new Set([
    "signal",
    "signals",
    "recentsignals",
    "payload",
    "user",
    "profile",
    "onboardingprofile",
    "context",
    "usercontext",
    "decision",
    "completion",
    "completions",
    "brief",
    "meal",
    "goal",
    "goals",
    // Capture prompts: the log target and the member's medical context. Never
    // logged today, but a capture id like "glp1_dose"/"cardio_bp" discloses a
    // medication/condition, so mask the whole subtree if any line ever emits it.
    "capture",
    "preselect",
    "medicalcontext",
    "conditions",
    // Confounders: a member's reason a reading dipped (illness, caregiving) is
    // health-adjacent, so mask a whole confounder object if any line emits one.
    "confounder",
]);
// Tier 2: leaf values masked wherever the key appears, even flattened.
// Health markers, free text, and credentials.
const LEAF_KEYS = new Set([
    "displayname",
    "name",
    "email",
    "note",
    "text",
    "rationale",
    "medication",
    "injectionsite",
    "bpm",
    "mmhg",
    "mgdl",
    "rmssd_ms",
    "baseline_ms",
    "z_score",
    "bmi",
    "bodyfatpercentage",
    "kg",
    "hba1c",
    "glucose",
    "capturepromptid",
    "logaction",
    // Confounder reason(s): "illness" / "caregiving" disclose health context.
    // Precise keys, not a bare "reason", which is a common non-PHI field.
    "confounderreason",
    "confounderreasons",
    "stages",
    "efficiency",
    "qualityscore",
    "sentiment",
    "authorization",
    "cookie",
    "token",
    "password",
    "secret",
]);
// HTTP headers redacted wherever a header map is logged. Mirrors the
// tkt-chassis HIDDEN_REQUEST_HEADERS set.
export const HIDDEN_HEADERS = new Set(["authorization", "cookie", "set-cookie"]);
// Highest-risk literal that leaks into free-text error messages. We cannot
// key-match a bare string, so a small value pattern covers the common case
// (a DB or validation error echoing a user email).
const EMAIL_RE = /[\w.+-]+@[\w-]+\.[\w.-]+/g;
function scrubText(text) {
    return text.replace(EMAIL_RE, REDACTED);
}
function redactError(err) {
    // Keep name and stack for debuggability; scrub both message and stack, which
    // are the fields most likely to echo row values from a database error.
    return {
        name: err.name,
        message: scrubText(err.message),
        stack: err.stack ? scrubText(err.stack) : err.stack,
    };
}
function redactValue(value, depth) {
    if (value === null || value === undefined)
        return value;
    if (value instanceof Error)
        return redactError(value);
    if (value instanceof Date)
        return value.toISOString();
    // Scrub emails from every string value. Errors are frequently flattened to
    // plain string fields (e.g. errorLogFields -> { error, errorCause, ... }),
    // so masking only Error instances would let an emailed DB message through.
    if (typeof value === "string")
        return scrubText(value);
    const type = typeof value;
    if (type === "number" || type === "boolean" || type === "bigint")
        return value;
    if (type !== "object")
        return value; // function, symbol
    if (depth >= MAX_DEPTH)
        return TRUNCATED;
    if (Array.isArray(value)) {
        const out = value.slice(0, MAX_ENTRIES).map((item) => redactValue(item, depth + 1));
        if (value.length > MAX_ENTRIES)
            out.push(TRUNCATED);
        return out;
    }
    return redactObject(value, depth + 1);
}
function redactObject(obj, depth) {
    const out = {};
    let count = 0;
    for (const [key, val] of Object.entries(obj)) {
        if (count >= MAX_ENTRIES) {
            out["..."] = TRUNCATED;
            break;
        }
        count++;
        const lower = key.toLowerCase();
        if (CONTAINER_KEYS.has(lower) || LEAF_KEYS.has(lower)) {
            out[key] = REDACTED;
            continue;
        }
        out[key] = redactValue(val, depth);
    }
    return out;
}
/**
 * Returns a masked copy of a log context object. Pure; never mutates input.
 */
export function redact(context) {
    return redactObject(context, 0);
}
/**
 * Returns a copy of an HTTP header map with sensitive headers replaced by
 * `***`. Case-insensitive on header name.
 */
export function redactHeaders(headers, hidden = HIDDEN_HEADERS) {
    const out = {};
    for (const [key, val] of Object.entries(headers)) {
        out[key] = hidden.has(key.toLowerCase()) ? "***" : val;
    }
    return out;
}
/**
 * Whether log redaction is active. Always on in production; the
 * `LOG_REDACTION=off` escape hatch is honoured only outside production so
 * PHI masking can never be disabled on a live deployment.
 */
export function redactionEnabled() {
    if (process.env.NODE_ENV === "production")
        return true;
    return process.env.LOG_REDACTION !== "off";
}
//# sourceMappingURL=redact.js.map